import { API } from '@/src/api/api'
import { queryKeys } from '@/src/api/queryKeys'
import type {
  IFetchOneCustomTimeSeriesMetaDataResponse,
  TFetchOneCustomTimeSeriesDataResponse,
} from '@/src/api/types/backend/custom-data.api'
import { ONE_MINUTE_IN_MS } from '@/src/utils/constants'
import { useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'

interface IQueryFnReturn {
  data: TFetchOneCustomTimeSeriesDataResponse
  metadata: IFetchOneCustomTimeSeriesMetaDataResponse | null
}

const initialData = {
  data: [],
  metadata: null,
}

export const useCustomTimeseriesData = ({ timeSeriesId }: { timeSeriesId?: string | null }) => {
  const queryKey = queryKeys.analytics.customTimeSeries.oneById.toKeyWithArgs(String(timeSeriesId))
  const query = useQuery<IQueryFnReturn, any, IQueryFnReturn>({
    queryKey,
    enabled: !!timeSeriesId,
    staleTime: ONE_MINUTE_IN_MS * 5,
    queryFn: async () => {
      if (!timeSeriesId) return { data: [], metadata: null }
      const metadataResponse = await API.customTimeSeriesMetadata.fetchOne({ id: timeSeriesId })
      const locationId = metadataResponse.data.location_id
      const dataResponse = await API.customTimeSeriesData.fetchOne({ locationId, timeSeriesId })
      return { data: dataResponse.data as any, metadata: metadataResponse.data as any }
    },

    select: useCallback(
      ({ data, metadata }): IQueryFnReturn => {
        const newMetadata = {
          ...metadata,
          granularity: metadata.resolution.enum,
          aggregationType: metadata.aggregation_type,
        }
        return { data, metadata: newMetadata }
      },
      [timeSeriesId]
    ),
  })

  return { ...query, data: query.data ?? initialData }
}
